// Resume Matching Functions - Client-side wrapper for Firebase callable functions
// All AI processing happens server-side for security (API keys not exposed to client)

import { getFunctions, httpsCallable } from 'firebase/functions';
import type {
  SaveLLMConfigRequest,
  SaveLLMConfigResponse,
  GenerateResumeRequest,
  GenerateResumeResponse,
  RecompileResumeRequest,
  RecompileResumeResponse,
  GetResumeSourceRequest,
  GetResumeSourceResponse,
} from '@shared/types';

/**
 * Job description analysis result
 */
export interface JDAnalysis {
  skills: string[];
  keywords: string[];
  suggestedPhrases: string[];
  roleRequirements: string[];
}

/**
 * Match result from the server
 */
export interface MatchResult {
  resumeId: string;
  resumeName: string;
  downloadUrl: string;
  matchScore: number;
  skillMatchScore: number;
  embeddingScore: number;
  matchedSkills: string[];
  missingSkills: string[];
  matchedKeywords: string[];
  missingKeywords: string[];
  experienceGaps: string[];
  hasEmbedding: boolean;
  targetRole?: string;
  experienceLevel?: string;
  resumeSummary?: string;
}

/**
 * Full match response including JD analysis
 */
export interface MatchResponse {
  results: MatchResult[];
  jdAnalysis: JDAnalysis;
}

/**
 * Batch process result for a single resume
 */
export interface BatchProcessResult {
  resumeId: string;
  name: string;
  status: 'success' | 'failed' | 'skipped';
  reason?: string;
  skillsCount?: number;
  hasEmbedding?: boolean;
}

/**
 * Batch process response
 */
export interface BatchProcessResponse {
  processed: number;
  failed: number;
  skipped: number;
  total: number;
  results: BatchProcessResult[];
}

/**
 * Process a newly uploaded resume - extract text, skills, and generate embedding.
 * Call this after uploading a resume file to Firebase Storage.
 *
 * @param resumeId - The SavedResume document ID in Firestore
 * @param storagePath - The path to the PDF in Firebase Storage
 * @returns Promise with extracted skills and embedding status
 */
export async function processResume(
  resumeId: string,
  storagePath: string
): Promise<{ skills: string[]; embeddingGenerated: boolean }> {
  const functions = getFunctions();
  const processResumeFn = httpsCallable<
    { resumeId: string; storagePath: string },
    { skills: string[]; embeddingGenerated: boolean }
  >(functions, 'process_resume');

  const result = await processResumeFn({ resumeId, storagePath });
  return result.data;
}

/**
 * Re-process an existing resume to update skills and embedding.
 * Useful when the AI model improves or user wants to refresh analysis.
 *
 * @param resumeId - The SavedResume document ID in Firestore
 * @returns Promise with extracted skills and embedding status
 */
export async function reprocessResume(
  resumeId: string
): Promise<{ skills: string[]; embeddingGenerated: boolean }> {
  const functions = getFunctions();
  const reprocessResumeFn = httpsCallable<
    { resumeId: string },
    { skills: string[]; embeddingGenerated: boolean }
  >(functions, 'reprocess_resume');

  const result = await reprocessResumeFn({ resumeId });
  return result.data;
}

/**
 * Match all user's saved resumes against a job description.
 * Returns ranked results with match scores, matched/missing skills, and JD analysis.
 *
 * @param jobDescription - The full job description text
 * @returns Promise with match results and JD analysis
 */
export async function matchResumesToJob(
  jobDescription: string
): Promise<MatchResponse> {
  const functions = getFunctions();
  const matchResumesFn = httpsCallable<
    { jobDescription: string },
    MatchResponse
  >(functions, 'match_resumes_to_job');

  const result = await matchResumesFn({ jobDescription });
  return result.data;
}

/**
 * Analyze a job description for an application — extracts skills, keywords, phrases, and requirements.
 * Stores the analysis results on the application document in Firestore.
 *
 * @param applicationId - The application document ID
 * @returns Promise with JD analysis
 */
export async function analyzeJobDescription(
  applicationId: string
): Promise<JDAnalysis> {
  const functions = getFunctions();
  const analyzeFn = httpsCallable<
    { applicationId: string },
    JDAnalysis
  >(functions, 'analyze_job_description_endpoint');

  const result = await analyzeFn({ applicationId });
  return result.data;
}

/**
 * Batch process all unprocessed resumes for the user.
 * More cost-effective than processing one by one.
 *
 * @returns Promise with batch processing results
 */
export async function batchProcessResumes(): Promise<BatchProcessResponse> {
  const functions = getFunctions();
  const batchFn = httpsCallable<
    Record<string, never>,
    BatchProcessResponse
  >(functions, 'batch_process_resumes');

  const result = await batchFn({});
  return result.data;
}

/**
 * Match result for application-specific matching
 */
export interface ApplicationMatchResult {
  matchScore: number;
  skillMatchScore: number;
  embeddingScore: number;
  matchedSkills: string[];
  missingSkills: string[];
  matchedKeywords: string[];
  missingKeywords: string[];
  jdSkills: string[];
  jdKeywords: string[];
  hasEmbedding: boolean;
}

/**
 * Match a specific resume against an application's job description.
 * Stores the match results on the application document.
 *
 * @param applicationId - The application document ID
 * @param resumeId - The saved resume document ID
 * @returns Promise with match results
 */
export async function matchResumeToApplication(
  applicationId: string,
  resumeId: string
): Promise<ApplicationMatchResult> {
  const functions = getFunctions();
  const matchFn = httpsCallable<
    { applicationId: string; resumeId: string },
    ApplicationMatchResult
  >(functions, 'match_resume_to_application');

  const result = await matchFn({ applicationId, resumeId });
  return result.data;
}

/**
 * Save the user's LLM provider configuration (BYOK).
 * Keys are encrypted server-side; only non-sensitive metadata is returned.
 *
 * @param params - Provider, API key, and optional embedding config
 * @returns Non-sensitive config metadata confirming what was saved
 */
export async function saveLLMConfig(params: SaveLLMConfigRequest): Promise<SaveLLMConfigResponse> {
  const functions = getFunctions();
  const fn = httpsCallable<SaveLLMConfigRequest, SaveLLMConfigResponse>(
    functions,
    'save_llm_config'
  );
  const result = await fn(params);
  return result.data;
}

// ---------------------------------------------------------------------------
// Resume Generation
// ---------------------------------------------------------------------------

/**
 * Trigger resume generation for a specific job application.
 * The cloud function reads the user's default profile resumeGenConfig,
 * tailors the resume to the application's JD + ATS suggestions,
 * compiles Typst to PDF, validates, and returns a signed download URL.
 *
 * @param applicationId - The application document ID whose JD is used for tailoring
 * @param instructionOverride - Optional per-session instructions (overrides defaultInstructions for this run only)
 */
export async function generateResume(
  applicationId: string,
  instructionOverride?: string
): Promise<GenerateResumeResponse> {
  const functions = getFunctions();
  const fn = httpsCallable<GenerateResumeRequest, GenerateResumeResponse>(
    functions,
    'generate_resume',
    { timeout: 300_000 }  // 5 min — generation + compile can be slow
  );
  const result = await fn({ applicationId, instructionOverride });
  return result.data;
}

/**
 * Re-compile a Typst resume source into a PDF.
 *
 * @param params - applicationId, generatedResumeId, and the modified typstSource
 */
export async function recompileResume(
  params: RecompileResumeRequest
): Promise<RecompileResumeResponse> {
  const functions = getFunctions();
  const fn = httpsCallable<RecompileResumeRequest, RecompileResumeResponse>(
    functions,
    'recompile_resume',
    { timeout: 120_000 }
  );
  const result = await fn(params);
  return result.data;
}
/**
 * Retrieves the Typst source code for a generated resume via backend proxy (CORS safe).
 */
export const getResumeSource = async (data: GetResumeSourceRequest): Promise<GetResumeSourceResponse> => {
  const functions = getFunctions();
  const getResumeSourceFn = httpsCallable<GetResumeSourceRequest, GetResumeSourceResponse>(
    functions,
    'get_resume_source'
  );

  try {
    const result = await getResumeSourceFn(data);
    return result.data;
  } catch (error: any) {
    console.error('Error fetching resume source via proxy:', error);
    throw new Error(error.message || 'Failed to fetch resume source');
  }
};
